import { haversine, distanceAlongRoute } from "../geo";
import type { LatLng, RouteStep } from "../types";
import type { RouteContext } from "../routing";

// Re-export so tests and live.ts can avoid importing routing.ts (which pulls in leaflet)
export type { RouteContext };
export { distanceAlongRoute };

/**

/**
 * Build per-segment duration array by matching geometry vertices to OSRM step
 * guide-points. For each geometry segment we find the nearest step endpoint and
 * use that step's speed (duration/m) to estimate the segment's travel time.
 *
 * Returns an array of length geometry.length - 1.
 */
export function buildSegmentTimes(
  geometry: LatLng[],
  steps?: RouteStep[],
  durationS?: number,
): number[] {
  if (!steps || steps.length === 0) {
    // Fallback: split total duration proportionally to segment length
    if (geometry.length < 2) return [];
    const segLens: number[] = [];
    let totalLen = 0;
    for (let i = 1; i < geometry.length; i++) {
      const l = haversine(geometry[i - 1], geometry[i]);
      segLens.push(l);
      totalLen += l;
    }
    if (totalLen === 0) return segLens.map(() => 0);
    if (durationS === undefined) return segLens.map(l => l / totalLen); // fractions summing to 1
    return segLens.map(l => (l / totalLen) * durationS);
  }

  const result: number[] = [];
  for (let i = 1; i < geometry.length; i++) {
    const v = geometry[i];
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let j = 0; j < steps.length; j++) {
      const [slng, slat] = steps[j].location;
      const d = haversine(v, { lat: slat, lng: slng });
      if (d < bestDist) { bestDist = d; bestIdx = j; }
    }
    const step  = steps[bestIdx];
    const speed = step.distanceM > 0 ? step.durationS / step.distanceM : 0;
    const segLen = haversine(geometry[i - 1], v);
    result.push(speed * segLen);
  }
  return result;
}

/**
 * Reconstruct the actual lat/lng point on the route polyline at distFromStartM.
 * Loops through geometry computing Haversine segment lengths.
 */
export function pointAtDistance(
  distFromStart: number,
  geometry: LatLng[],
): { pt: LatLng; distFromStart: number } {
  let cumDist = 0;
  for (let i = 1; i < geometry.length; i++) {
    const a = geometry[i - 1];
    const b = geometry[i];
    const segLen = haversine(a, b);
    const segEnd  = cumDist + segLen;
    if (distFromStart <= segEnd || i === geometry.length - 1) {
      const t = segLen === 0 ? 0 : Math.max(0, Math.min(1, (distFromStart - cumDist) / segLen));
      return {
        pt: { lat: a.lat + t * (b.lat - a.lat), lng: a.lng + t * (b.lng - a.lng) },
        distFromStart,
      };
    }
    cumDist = segEnd;
  }
  return { pt: geometry[geometry.length - 1], distFromStart };
}

/**
 * Compute elapsed travel time from route start to a given position on the route.
 *
 * Mirrors the logic of remainingTime() but from the start direction.
 * Uses per-segment real durations from routeCtx.segmentTimes.
 * The current (partially-traversed) segment is linearly interpolated;
 * all preceding complete segments use their full real durations.
 *
 * @param distFromStartM  Distance from route start to the nearest point on the route.
 * @param routeCtx         Must have routeCtx.segmentTimes populated.
 * @returns Elapsed seconds.
 */
export function elapsedTime(distFromStartM: number, routeCtx: RouteContext): number {
  const segTimes = routeCtx.segmentTimes;
  const n = segTimes.length;
  if (n === 0) return 0;

  let cumDist = 0;
  for (let i = 0; i < n; i++) {
    const segLen  = haversine(routeCtx.geometry[i], routeCtx.geometry[i + 1]);
    const segEnd  = cumDist + segLen;

    if (distFromStartM <= segEnd || i === n - 1) {
      // We're in (or past) this segment — interpolate the portion already covered
      const t = segLen === 0 ? 0 : Math.max(0, Math.min(1, (distFromStartM - cumDist) / segLen));
      const elapsedInSeg = t * segTimes[i];
      const prior = segTimes.slice(0, i).reduce((s, d) => s + d, 0);
      return Math.round(elapsedInSeg + prior);
    }

    cumDist = segEnd;
  }

  return Math.round(routeCtx.segmentTimes.reduce((s, d) => s + d, 0));
}

/**
 * Compute remaining travel time after a given position on the route.
 *
 * Uses per-segment real durations from routeCtx.segmentTimes.
 * Only the current (partially-traversed) segment is linearly interpolated.
 * All subsequent complete segments use their full real durations.
 *
 * @param distFromStartM  Distance from route start to the nearest point on the route.
 * @param routeCtx        Must have routeCtx.segmentTimes populated.
 * @returns Remaining seconds (floored at 0).
 */
export function remainingTime(distFromStartM: number, routeCtx: RouteContext): number {
  const segTimes = routeCtx.segmentTimes;
  const n = segTimes.length;

  if (n === 0) return 0;

  let cumDist = 0;

  for (let i = 0; i < n; i++) {
    const segLen  = haversine(routeCtx.geometry[i], routeCtx.geometry[i + 1]);
    const segEnd  = cumDist + segLen;

    if (distFromStartM < segEnd || i === n - 1) {
      // We're in (or past) this segment — interpolate the remaining portion
      const t = segLen === 0 ? 0 : Math.max(0, Math.min(1, (segEnd - distFromStartM) / segLen));
      const remainingInSeg = t * segTimes[i];
      const subsequent     = segTimes.slice(i + 1).reduce((s, d) => s + d, 0);
      return Math.max(0, Math.round(remainingInSeg + subsequent));
    }

    cumDist = segEnd;
  }

  return 0;
}